import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { GitCompare, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { VersionRecord } from './VersionHistory';

interface VersionCompareProps {
  standardId: string;
  currentVersion: string;
}

interface DiffResult {
  added: string[];
  removed: string[];
  changed: Array<{ field: string; from: unknown; to: unknown }>;
}

function compareObjects(from: Record<string, unknown>, to: Record<string, unknown>): DiffResult {
  const fromKeys = Object.keys(from);
  const toKeys = Object.keys(to);
  
  return {
    added: toKeys.filter(k => !fromKeys.includes(k)),
    removed: fromKeys.filter(k => !toKeys.includes(k)),
    changed: fromKeys
      .filter(k => toKeys.includes(k) && JSON.stringify(from[k]) !== JSON.stringify(to[k]))
      .map(k => ({ field: k, from: from[k], to: to[k] })),
  };
}

export function VersionCompare({ standardId, currentVersion }: VersionCompareProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<VersionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromVersion, setFromVersion] = useState<string>('');
  const [toVersion, setToVersion] = useState<string>('');
  const [activeTab, setActiveTab] = useState('schema');

  useEffect(() => {
    if (open && standardId) {
      fetchVersions();
    }
  }, [open, standardId]);

  const fetchVersions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('data_standard_versions')
      .select('*')
      .eq('standard_id', standardId)
      .order('published_at', { ascending: false });

    if (error) {
      toast({ title: 'Error fetching versions', description: error.message, variant: 'destructive' });
    } else if (data && data.length > 0) {
      const versionsData = data.map(v => ({
        id: v.id,
        standard_id: v.standard_id,
        version: v.version,
        schema: v.schema as object,
        example: v.example as object,
        fields: v.fields as object[],
        changelog: v.changelog,
        published_at: v.published_at,
        published_by: v.published_by,
        is_current: v.is_current,
      }));
      setVersions(versionsData);
      
      // Set defaults if more than one version
      if (versionsData.length >= 2) {
        setFromVersion(versionsData[1].version);
        setToVersion(versionsData[0].version);
      } else if (versionsData.length === 1) {
        setFromVersion(versionsData[0].version);
        setToVersion(versionsData[0].version);
      }
    }
    setLoading(false);
  };

  const fromVersionData = useMemo(() => 
    versions.find(v => v.version === fromVersion), 
    [versions, fromVersion]
  );
  
  const toVersionData = useMemo(() => 
    versions.find(v => v.version === toVersion), 
    [versions, toVersion]
  );

  const schemaDiff = useMemo(() => {
    if (!fromVersionData || !toVersionData) return null;
    const fromProps = (fromVersionData.schema as Record<string, unknown>)?.properties as Record<string, unknown> || {};
    const toProps = (toVersionData.schema as Record<string, unknown>)?.properties as Record<string, unknown> || {};
    return compareObjects(fromProps, toProps);
  }, [fromVersionData, toVersionData]);

  const exampleDiff = useMemo(() => {
    if (!fromVersionData || !toVersionData) return null;
    return compareObjects(
      fromVersionData.example as Record<string, unknown>, 
      toVersionData.example as Record<string, unknown>
    );
  }, [fromVersionData, toVersionData]);

  const renderDiff = (diff: DiffResult | null) => {
    if (!diff) return null;

    const hasChanges = diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0;

    if (!hasChanges) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No differences found between these versions.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="flex flex-wrap gap-2">
          {diff.added.length > 0 && (
            <Badge className="bg-green-500/10 text-green-600 border border-green-500/20">
              +{diff.added.length} added
            </Badge>
          )}
          {diff.removed.length > 0 && (
            <Badge className="bg-red-500/10 text-red-600 border border-red-500/20">
              -{diff.removed.length} removed
            </Badge>
          )}
          {diff.changed.length > 0 && (
            <Badge className="bg-yellow-500/10 text-yellow-600 border border-yellow-500/20">
              ~{diff.changed.length} modified
            </Badge>
          )}
        </div>

        {/* Added fields */}
        {diff.added.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-green-600">Added</p>
            <div className="space-y-1">
              {diff.added.map(field => (
                <div key={field} className="px-3 py-2 bg-green-500/5 border border-green-500/20 rounded text-sm font-mono">
                  + {field}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Removed fields */}
        {diff.removed.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-red-600">Removed</p>
            <div className="space-y-1">
              {diff.removed.map(field => (
                <div key={field} className="px-3 py-2 bg-red-500/5 border border-red-500/20 rounded text-sm font-mono">
                  - {field}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Changed fields */}
        {diff.changed.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-yellow-600">Modified</p>
            <div className="space-y-2">
              {diff.changed.map(({ field, from, to }) => (
                <div key={field} className="px-3 py-2 bg-yellow-500/5 border border-yellow-500/20 rounded">
                  <p className="text-sm font-mono font-medium mb-2">~ {field}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-muted-foreground mb-1">From:</p>
                      <pre className="bg-muted/50 p-2 rounded overflow-auto max-h-32">
                        {JSON.stringify(from, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">To:</p>
                      <pre className="bg-muted/50 p-2 rounded overflow-auto max-h-32">
                        {JSON.stringify(to, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <GitCompare className="h-4 w-4 mr-2" />
          Compare Versions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Compare Versions</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : versions.length < 2 ? (
          <div className="text-center py-8 text-muted-foreground">
            At least 2 versions are needed to compare.
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Version Selectors */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1 space-y-1">
                <p className="text-xs text-muted-foreground">From</p>
                <Select value={fromVersion} onValueChange={setFromVersion}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select version" />
                  </SelectTrigger>
                  <SelectContent>
                    {versions.map(v => (
                      <SelectItem key={v.id} value={v.version}>
                        v{v.version} {v.is_current && '(current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="pt-4">
                <span className="text-muted-foreground">→</span>
              </div>
              
              <div className="flex-1 space-y-1">
                <p className="text-xs text-muted-foreground">To</p>
                <Select value={toVersion} onValueChange={setToVersion}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select version" />
                  </SelectTrigger>
                  <SelectContent>
                    {versions.map(v => (
                      <SelectItem key={v.id} value={v.version}>
                        v{v.version} {v.is_current && '(current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Diff Display */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList>
                <TabsTrigger value="schema">Schema Diff</TabsTrigger>
                <TabsTrigger value="example">Example Diff</TabsTrigger>
              </TabsList>
              <ScrollArea className="flex-1 mt-4">
                <TabsContent value="schema" className="m-0">
                  {renderDiff(schemaDiff)}
                </TabsContent>
                <TabsContent value="example" className="m-0">
                  {renderDiff(exampleDiff)}
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
